import { forwardRef, useCallback, useEffect, useState } from 'react';
import { Button, HStack, Spinner, Text } from '@chakra-ui/react';
import { DialogBody, DialogCloseTrigger, DialogContent, DialogFooter, DialogHeader, DialogRoot, DialogTitle } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { NumberInputField, NumberInputRoot } from '@/components/ui/number-input';
import { isValidTransitionBandPercent } from '@/utils/network/transitionFunctions';
export type TransitionDefaultsDialogProps = {
  isOpen: boolean;
  networkName: string;
  /** The network's current default band. Undefined while it is still loading. */
  halfBandPercent: number | undefined;
  onClose: () => void;
  onSubmit: (halfBandPercent: number) => Promise<void>;
};

// KBE-769: edits the network-wide default transition band. Every semi-continuous
// variable that has no custom values of its own resolves its curve from this, so
// saving here retroactively moves those variables' inference output — which is
// why the copy says so rather than presenting this as a cosmetic preference.
//
// KBE-772 removed the shape selector: there is one curve now (a log-logistic,
// symmetric at the default's mirrored values), so the shape had nothing left to
// select. The default stays a PERCENTAGE while per-variable values are absolute,
// because one percentage is scale-appropriate across every variable in the
// network.
const TransitionDefaultsDialog = forwardRef<HTMLDivElement, TransitionDefaultsDialogProps>(({
  isOpen,
  networkName,
  halfBandPercent,
  onClose,
  onSubmit
}, ref) => {
  // Kept as raw text for the same reason the per-variable fields are: an
  // in-progress "0." parses to 0, and rendering that back would swallow the
  // decimal point and make a fractional percentage impossible to type.
  const [percentDraft, setPercentDraft] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Adopt the loaded value once it arrives, without clobbering an edit already in
  // progress (the fetch can settle after the dialog is interactive).
  useEffect(() => {
    if (halfBandPercent === undefined) return;
    setPercentDraft(current => current ?? String(halfBandPercent));
  }, [halfBandPercent]);
  const parsedPercent = parseFloat(percentDraft ?? '');
  const percentValid = isValidTransitionBandPercent(parsedPercent);
  const canSubmit = percentValid && !isSaving;
  const handleClose = useCallback(() => {
    onClose();
    setPercentDraft(null);
    setIsSaving(false);
  }, [onClose]);
  const handleSubmit = useCallback(async () => {
    if (!percentValid) return;
    setIsSaving(true);
    try {
      await onSubmit(parsedPercent);
      handleClose();
    } finally {
      setIsSaving(false);
    }
  }, [percentValid, parsedPercent, onSubmit, handleClose]);
  return <DialogRoot open={isOpen} onOpenChange={e => {
    if (!e.open) handleClose();
  }}>
      <DialogContent ref={ref}>
        <DialogHeader>
          <DialogTitle>Default transition band</DialogTitle>
        </DialogHeader>
        <DialogCloseTrigger />
        <DialogBody>
          <Text color="gray.500" fontSize="sm" mb={3}>
            For {networkName}
          </Text>
          {percentDraft === null ? <Spinner size="sm" /> : <>
              <Field errorText="Enter 0 or more. 0 means a hard cutoff with no smoothing." invalid={!percentValid} label="Band (± % of each boundary)">
                <HStack>
                  <Text flexShrink={0} fontSize="sm">
                    ±
                  </Text>
                  <NumberInputRoot min={0} size="sm" step={1} value={percentDraft} width="6.5rem" onValueChange={details => setPercentDraft(details.value)}>
                    <NumberInputField aria-label="default-transition-half-band-percent" onKeyDown={e_0 => {
                  if (e_0.key === 'Enter' && canSubmit) void handleSubmit();
                }} />
                  </NumberInputRoot>
                  <Text flexShrink={0} fontSize="sm">
                    %
                  </Text>
                </HStack>
              </Field>
              <Text color="gray.500" fontSize="sm" mt={3}>
                Puts the 5% and 95% points this far either side of each state
                boundary, as a share of that boundary&apos;s own value. Applies
                to every semi-continuous variable in this network that does not
                define its own values, and takes effect immediately.
                {percentValid && parsedPercent === 0 ? ' At 0 those variables get a hard cutoff: no smoothing, and each value snaps to the state whose interval claims it.' : ''}
              </Text>
            </>}
        </DialogBody>
        <DialogFooter>
          <Button disabled={!canSubmit} onClick={() => void handleSubmit()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>;
});
TransitionDefaultsDialog.displayName = 'TransitionDefaultsDialog';
export default TransitionDefaultsDialog;